'use client';

import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { usePageBuilder } from '@/lib/hooks/use-page-builder';
import { useAsyncUploads, type AsyncUploadsApi } from '@/lib/hooks/use-async-uploads';
import type {
  PageBuilderFormData,
  PageBuilderHeader,
  PageBuilderMetadata,
  ContentBlock,
  BlockType,
} from '@/lib/types/page-builder';
import type { UserSearchResult } from '@/lib/types/team';
import type { CategoryDto } from '@/lib/validations/product-submission';
import { getCategories } from '@/lib/services/productService';

// ============================================================================
// CONTEXT TYPE
// ============================================================================

interface PageBuilderContextValue {
  // Editing mode — 'create' (new submission) or 'edit' (existing product).
  // Drives draft persistence, submit copy, and immutable-field locking.
  mode: 'create' | 'edit';

  // Form data
  formData: PageBuilderFormData;

  // Header operations
  updateHeader: <K extends keyof PageBuilderHeader>(
    field: K,
    value: PageBuilderHeader[K]
  ) => void;

  // Block operations
  addBlock: (type: BlockType, afterId?: string) => string;
  updateBlock: (id: string, updates: Partial<ContentBlock>) => void;
  updateBlockWith: (
    id: string,
    updater: (block: ContentBlock) => Partial<ContentBlock>
  ) => void;
  deleteBlock: (id: string) => void;
  moveBlock: (fromIndex: number, toIndex: number) => void;
  reorderBlocks: (newBlocks: ContentBlock[]) => void;
  duplicateBlock: (id: string) => string | null;
  getBlockById: (id: string) => ContentBlock | undefined;
  getBlockIndex: (id: string) => number;

  // Metadata operations
  updateMetadata: <K extends keyof PageBuilderMetadata>(
    field: K,
    value: PageBuilderMetadata[K]
  ) => void;
  updateMetadataWith: <K extends keyof PageBuilderMetadata>(
    field: K,
    updater: (value: PageBuilderMetadata[K]) => PageBuilderMetadata[K]
  ) => void;

  // Async image uploads (optimistic previews with background upload)
  uploads: AsyncUploadsApi['uploads'];
  startUpload: AsyncUploadsApi['startUpload'];
  retryUpload: AsyncUploadsApi['retryUpload'];
  dismissUpload: AsyncUploadsApi['dismissUpload'];
  hasActiveUploads: boolean;

  // Pending invitation operations
  addPendingInvitation: (user: UserSearchResult, role: string) => void;
  removePendingInvitation: (userId: number) => void;
  updatePendingInvitationRole: (userId: number, role: string) => void;

  // Undo/Redo
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // State
  isDirty: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  errors: Map<string, string[]>;

  // Categories (loaded from API)
  categories: CategoryDto[];
  categoriesLoading: boolean;

  // Validation
  validate: () => boolean;

  // Actions
  reset: () => void;
  submit: () => Promise<void>;

  // Focus management
  focusedBlockId: string | null;
  setFocusedBlockId: (id: string | null) => void;

  // Insert menu
  isInsertMenuOpen: boolean;
  insertMenuAnchorId: string | null;
  openInsertMenu: (afterBlockId?: string) => void;
  closeInsertMenu: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const PageBuilderContext = createContext<PageBuilderContextValue | null>(null);

// ============================================================================
// PROVIDER PROPS
// ============================================================================

interface PageBuilderProviderProps {
  children: ReactNode;
  initialData?: Partial<PageBuilderFormData>;
  onSubmit?: (data: PageBuilderFormData) => Promise<void>;
  onAutoSave?: (data: PageBuilderFormData) => void;
  /** 'create' (default) persists a localStorage draft; 'edit' does not. */
  mode?: 'create' | 'edit';
}

// ============================================================================
// DRAFT STORAGE
// ============================================================================

const PAGE_BUILDER_DRAFT_KEY = 'page_builder_draft_v1';

function saveDraftToStorage(data: PageBuilderFormData): void {
  try {
    const draft = {
      ...data,
      savedAt: Date.now(),
      version: 1,
    };
    localStorage.setItem(PAGE_BUILDER_DRAFT_KEY, JSON.stringify(draft));
  } catch (e) {
    console.error('Failed to save draft:', e);
  }
}

function loadDraftFromStorage(): Partial<PageBuilderFormData> | null {
  try {
    const stored = localStorage.getItem(PAGE_BUILDER_DRAFT_KEY);
    if (!stored) return null;

    const draft = JSON.parse(stored);

    // Check if draft is too old (7 days)
    const maxAge = 7 * 24 * 60 * 60 * 1000;
    if (Date.now() - draft.savedAt > maxAge) {
      localStorage.removeItem(PAGE_BUILDER_DRAFT_KEY);
      return null;
    }

    return draft;
  } catch (e) {
    console.error('Failed to load draft:', e);
    return null;
  }
}

function clearDraftFromStorage(): void {
  try {
    localStorage.removeItem(PAGE_BUILDER_DRAFT_KEY);
  } catch (e) {
    console.error('Failed to clear draft:', e);
  }
}

// ============================================================================
// PROVIDER (outer shell — loads draft, then mounts inner provider)
// ============================================================================

export function PageBuilderProvider({
  children,
  initialData,
  onSubmit,
  onAutoSave,
  mode = 'create',
}: PageBuilderProviderProps) {
  const [resolvedInitialData, setResolvedInitialData] = useState<
    Partial<PageBuilderFormData> | undefined
  >(undefined);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (initialData) {
      setResolvedInitialData(initialData);
    } else if (mode === 'create') {
      // Draft restore only applies to fresh submissions, never to edits.
      const draft = loadDraftFromStorage();
      if (draft) {
        setResolvedInitialData(draft);
        toast('С возвращением!', {
          description: 'Ваш черновик восстановлен',
        });
      }
    }
    setReady(true);
  }, [initialData, mode]);

  if (!ready) {
    return null;
  }

  return (
    <PageBuilderProviderInner
      key={ready ? 'ready' : 'loading'}
      initialData={resolvedInitialData}
      onSubmit={onSubmit}
      onAutoSave={onAutoSave}
      mode={mode}
    >
      {children}
    </PageBuilderProviderInner>
  );
}

// ============================================================================
// PROVIDER INNER (mounted only after draft is resolved)
// ============================================================================

function PageBuilderProviderInner({
  children,
  initialData,
  onSubmit,
  onAutoSave,
  mode = 'create',
}: PageBuilderProviderProps) {
  // usePageBuilder is called AFTER draft is loaded, so useState initializer gets correct data
  const pageBuilder = usePageBuilder({
    initialData,
    onAutoSave: (data) => {
      // Edits are not drafted — persisting here would clobber a create draft.
      if (mode === 'create') saveDraftToStorage(data);
      onAutoSave?.(data);
    },
    autoSaveInterval: 3000,
  });

  // Categories state
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Load categories
  useEffect(() => {
    async function fetchCategories() {
      setCategoriesLoading(true);
      const result = await getCategories();
      if (result.success && result.data) {
        setCategories(result.data);
      } else {
        toast.error('Не удалось загрузить категории');
      }
      setCategoriesLoading(false);
    }
    fetchCategories();
  }, []);

  // Async image uploads (shared across all upload spots in the builder)
  const asyncUploads = useAsyncUploads();
  const hasActiveUploads = asyncUploads.activeUploadCount > 0;

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Focus management
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(null);

  // Insert menu state
  const [isInsertMenuOpen, setIsInsertMenuOpen] = useState(false);
  const [insertMenuAnchorId, setInsertMenuAnchorId] = useState<string | null>(null);

  const openInsertMenu = useCallback((afterBlockId?: string) => {
    setInsertMenuAnchorId(afterBlockId ?? null);
    setIsInsertMenuOpen(true);
  }, []);

  const closeInsertMenu = useCallback(() => {
    setIsInsertMenuOpen(false);
    setInsertMenuAnchorId(null);
  }, []);

  // Submit handler
  const submit = useCallback(async () => {
    if (!onSubmit) return;

    // Images are uploaded in the background — never submit a half-baked page
    if (hasActiveUploads) {
      toast.error('Дождитесь загрузки изображений');
      return;
    }

    // Validate first
    const isValid = pageBuilder.validate();
    if (!isValid) {
      toast.error('Исправьте ошибки перед отправкой');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(pageBuilder.formData);
      if (mode === 'create') {
        clearDraftFromStorage();
        toast.success('Продукт успешно отправлен!');
      } else {
        toast.success('Изменения сохранены');
      }
    } catch (error) {
      toast.error(mode === 'edit' ? 'Не удалось сохранить изменения' : 'Ошибка при отправке');
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, pageBuilder, hasActiveUploads, mode]);

  const value: PageBuilderContextValue = {
    ...pageBuilder,
    mode,
    uploads: asyncUploads.uploads,
    startUpload: asyncUploads.startUpload,
    retryUpload: asyncUploads.retryUpload,
    dismissUpload: asyncUploads.dismissUpload,
    hasActiveUploads,
    isSubmitting,
    categories,
    categoriesLoading,
    submit,
    focusedBlockId,
    setFocusedBlockId,
    isInsertMenuOpen,
    insertMenuAnchorId,
    openInsertMenu,
    closeInsertMenu,
  };

  return (
    <PageBuilderContext.Provider value={value}>
      {children}
    </PageBuilderContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function usePageBuilderContext(): PageBuilderContextValue {
  const context = useContext(PageBuilderContext);
  if (!context) {
    throw new Error('usePageBuilderContext must be used within PageBuilderProvider');
  }
  return context;
}

// ============================================================================
// EXPORTS
// ============================================================================

export { PAGE_BUILDER_DRAFT_KEY, clearDraftFromStorage };
